/**
 * LogPaymentModal.jsx - Part 4 Implementation
 * 
 * Checklist Items Covered:
 * - I1: Amount Input
 * - I2: Date Input
 * - I3: Paid By Input
 * - I4: Payment Method Picker
 * - I5: Method Detail (card last 4, Zelle ID, etc.)
 * - I6: Note Field
 * - I7: Link Receipt
 * - I8: Balance Banner
 */

import React, { useState, useMemo } from 'react';
import {
  X, CreditCard, Banknote, Building2, Smartphone, DollarSign,
  Calendar, User, FileText, Link, AlertCircle, CheckCircle
} from 'lucide-react';
import {
  formatMoney,
  getVendorContract,
  getVendorPaid,
  getVendorRemaining,
  PAYMENT_METHOD_LABELS,
} from '../../lib/vendorInsights';
import { theme } from '../../data/colors';

const PAYMENT_METHODS = [
  { id: 'cash', label: 'Cash', icon: Banknote },
  { id: 'credit_card', label: 'Credit Card', icon: CreditCard },
  { id: 'debit_card', label: 'Debit Card', icon: CreditCard },
  { id: 'check', label: 'Check', icon: FileText },
  { id: 'bank_transfer', label: 'Bank Transfer / Zelle', icon: Building2 },
  { id: 'venmo', label: 'Venmo', icon: Smartphone },
  { id: 'paypal', label: 'PayPal', icon: DollarSign },
  { id: 'other', label: 'Other', icon: DollarSign },
];

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1100,
    animation: 'fadeIn 0.2s ease',
  },
  modal: {
    background: theme.bgCard,
    borderRadius: 14,
    width: '100%',
    maxWidth: 520,
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 40px 80px rgba(0,0,0,0.2)',
    border: `1px solid ${theme.borderMedium}`,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: `1px solid ${theme.borderLight}`,
  },
  title: {
    margin: 0,
    fontSize: 18,
    fontWeight: 700,
    color: theme.textPrimary,
  },
  subtitle: {
    fontSize: 12,
    color: theme.textMuted,
    marginTop: 2,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 4,
  },
  
  // I8: Balance Banner
  balanceBanner: {
    margin: '16px 24px',
    padding: '14px 16px',
    borderRadius: 14,
    background: theme.bgCardWarm,
    border: `1px solid ${theme.borderLight}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  balanceLabel: {
    fontSize: 11,
    color: theme.textMuted,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
  },
  balanceValue: {
    fontSize: 20,
    fontWeight: 700,
    color: '#C8783A',
  },
  balanceAfter: {
    marginTop: 16,
    padding: '12px 16px',
    borderRadius: 12,
    background: theme.statusSuccessTint,
    border: `1px solid rgba(47,122,87,0.18)`,
  },
  balanceAfterLabel: {
    fontSize: 10,
    color: theme.statusSuccess,
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  balanceAfterValue: {
    fontSize: 16,
    fontWeight: 700,
    color: theme.statusSuccess,
  },
  
  form: {
    padding: '0 24px 24px',
  },
  section: {
    marginTop: 20,
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: 700,
    color: theme.textMuted,
    marginBottom: 10,
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
  },
  
  // I1: Amount Input
  amountWrap: {
    position: 'relative',
  },
  amountPrefix: {
    position: 'absolute',
    left: 14,
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: 18,
    fontWeight: 600,
    color: theme.textMuted,
  },
  amountInput: {
    width: '100%',
    padding: '14px 14px 14px 32px',
    border: `1px solid ${theme.borderMedium}`,
    borderRadius: 12,
    fontSize: 24,
    fontWeight: 700,
    color: theme.textPrimary,
    background: theme.bgCard,
  },
  
  // I2, I3: Date & Paid By
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
  },
  fieldLabel: {
    display: 'block',
    fontSize: 11,
    color: theme.textMuted,
    marginBottom: 4,
    fontWeight: 600,
  },
  fieldInput: {
    width: '100%',
    padding: '10px 12px',
    border: `1px solid ${theme.borderMedium}`,
    borderRadius: 10,
    fontSize: 13,
    background: theme.bgCard,
    color: theme.textPrimary,
  },
  
  // I4: Payment Method Picker
  methodGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 8,
  },
  methodBtn: {
    padding: '12px 8px',
    border: `1px solid ${theme.borderMedium}`,
    borderRadius: 12,
    background: theme.bgCard,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    transition: 'all 0.15s',
  },
  methodBtnActive: {
    borderColor: theme.accentPrimary,
    background: theme.statusWarningTint,
  },
  methodIcon: {
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  methodLabel: {
    fontSize: 9,
    fontWeight: 600,
    color: theme.textSecondary,
    textAlign: 'center',
    lineHeight: '12px',
  },
  
  // I5: Method Detail
  methodDetail: {
    marginTop: 12,
    padding: '10px 12px',
    border: `1px solid ${theme.borderMedium}`,
    borderRadius: 10,
    fontSize: 13,
    background: theme.bgCard,
    color: theme.textPrimary,
    width: '100%',
  },
  
  // I6: Note
  noteInput: {
    width: '100%',
    padding: '10px 12px',
    border: `1px solid ${theme.borderMedium}`,
    borderRadius: 10,
    fontSize: 13,
    background: theme.bgCard,
    color: theme.textPrimary,
    resize: 'vertical',
    minHeight: 60,
  },
  
  // I7: Link Receipt
  linkReceiptBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 14px',
    border: '1px dashed rgba(198,167,90,0.3)',
    borderRadius: 10,
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 12,
    color: theme.textMuted,
    fontWeight: 600,
    width: '100%',
    justifyContent: 'center',
    transition: 'all 0.15s',
  },
  // Actions
  actions: {
    display: 'flex',
    gap: 12,
    marginTop: 24,
    paddingTop: 16,
    borderTop: `1px solid ${theme.borderLight}`,
  },
  cancelBtn: {
    flex: 1,
    padding: '12px 20px',
    background: 'transparent',
    border: `1px solid ${theme.borderMedium}`,
    borderRadius: 12,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    color: theme.textSecondary,
  },
  submitBtn: {
    flex: 2,
    padding: '12px 24px',
    background: theme.accentPrimary,
    border: 'none',
    borderRadius: 12,
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    color: theme.textInverse,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
};

const LogPaymentModal = ({ vendor, onClose, onSubmit }) => {
  const priorPayments = vendor?.payments?.length || 0;
  const [amount, setAmount] = useState(() => (priorPayments === 0 && Number(vendor?.deposit_amount) > 0) ? String(vendor.deposit_amount) : '');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [paidBy, setPaidBy] = useState('');
  const [method, setMethod] = useState('credit_card');
  const [methodDetail, setMethodDetail] = useState('');
  const [note, setNote] = useState('');
  const [isDeposit, setIsDeposit] = useState(priorPayments === 0);
  const [submitting, setSubmitting] = useState(false);

  const contract = getVendorContract(vendor);
  const paid = getVendorPaid(vendor);
  const remaining = getVendorRemaining(vendor);

  const parsedAmount = parseFloat(amount) || 0;
  const newBalance = Math.max(remaining - parsedAmount, 0);
  const willBeFullyPaid = parsedAmount >= remaining;

  const getMethodPlaceholder = () => {
    switch (method) {
      case 'credit_card':
      case 'debit_card':
        return 'Last 4 digits (e.g., 4242)';
      case 'check':
        return 'Check number';
      case 'bank_transfer':
        return 'Zelle ID or reference';
      case 'venmo':
        return 'Venmo username';
      case 'paypal':
        return 'PayPal email or ID';
      default:
        return 'Additional details';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!parsedAmount || parsedAmount <= 0) return;

    setSubmitting(true);
    try {
      await onSubmit({
        amount: parsedAmount,
        date,
        paid_by: paidBy,
        method,
        method_detail: methodDetail,
        note,
        is_deposit: isDeposit,
      });
    } catch (err) {
      console.error('Error logging payment:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()} data-testid="log-payment-modal">
        {/* Header */}
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>Log Payment</h2>
            <div style={styles.subtitle}>
              {vendor?.company_name} · {priorPayments === 0 ? 'First payment' : `Payment #${priorPayments + 1} · ${priorPayments} logged so far`}
            </div>
          </div>
          <button onClick={onClose} style={styles.closeBtn}>
            <X size={20} color="#8B8478" />
          </button>
        </div>

        {/* I8: Balance Banner */}
        <div style={styles.balanceBanner}>
          <div>
            <div style={styles.balanceLabel}>Open Balance</div>
            <div style={styles.balanceValue}>{formatMoney(remaining)}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={styles.balanceLabel}>Contract Total</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#6F685C' }}>{formatMoney(contract)}</div>
          </div>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          {/* I1: Amount Input */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Payment Amount</div>
            <div style={styles.amountWrap}>
              <span style={styles.amountPrefix}>$</span>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                style={styles.amountInput}
                step="0.01"
                min="0"
                required
                data-testid="payment-amount-input"
              />
            </div>
            
            {/* Balance After */}
            {parsedAmount > 0 && (
              <div style={styles.balanceAfter}>
                <div style={styles.balanceAfterLabel}>
                  {willBeFullyPaid ? 'Fully Paid!' : 'Balance After Payment'}
                </div>
                <div style={styles.balanceAfterValue}>
                  {willBeFullyPaid ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <CheckCircle size={18} /> Paid in Full
                    </span>
                  ) : (
                    formatMoney(newBalance)
                  )}
                </div>
              </div>
            )}

            {/* Deposit tag — clarifies the deposit vs. a regular installment */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, cursor: 'pointer', fontSize: 12.5, color: theme.textSecondary, fontWeight: 600 }}>
              <input
                type="checkbox"
                checked={isDeposit}
                onChange={(e) => setIsDeposit(e.target.checked)}
                style={{ width: 16, height: 16, accentColor: '#C6A75A', cursor: 'pointer' }}
                data-testid="payment-is-deposit"
              />
              Mark this payment as the deposit
            </label>
          </div>

          {/* I2, I3: Date & Paid By */}
          <div style={styles.section}>
            <div style={styles.grid}>
              <div>
                <label style={styles.fieldLabel}>Payment Date</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  style={styles.fieldInput}
                  required
                  data-testid="payment-date-input"
                />
              </div>
              <div>
                <label style={styles.fieldLabel}>Paid By</label>
                <input
                  type="text"
                  value={paidBy}
                  onChange={(e) => setPaidBy(e.target.value)}
                  placeholder="Who made the payment?"
                  style={styles.fieldInput}
                  data-testid="payment-paidby-input"
                />
              </div>
            </div>
          </div>

          {/* I4: Payment Method Picker */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Payment Method</div>
            <div style={styles.methodGrid}>
              {PAYMENT_METHODS.map((m) => {
                const IconComponent = m.icon;
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => { setMethod(m.id); setMethodDetail(''); }}
                    style={{
                      ...styles.methodBtn,
                      ...(method === m.id ? styles.methodBtnActive : {}),
                    }}
                    data-testid={`payment-method-${m.id}`}
                  >
                    <div style={styles.methodIcon}>
                      <IconComponent size={18} color={method === m.id ? '#C6A75A' : '#8B8478'} />
                    </div>
                    <span style={styles.methodLabel}>{m.label}</span>
                  </button>
                );
              })}
            </div>

            {/* I5: Method Detail */}
            {method && method !== 'cash' && (
              <input
                type="text"
                value={methodDetail}
                onChange={(e) => setMethodDetail(e.target.value)}
                placeholder={getMethodPlaceholder()}
                style={styles.methodDetail}
                data-testid="payment-method-detail"
              />
            )}
          </div>

          {/* I6: Note */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Note (Optional)</div>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add any additional notes about this payment..."
              style={styles.noteInput}
              data-testid="payment-note-input"
            />
          </div>

          {/* I7: Link Receipt — DISABLED until receipt upload is actually wired (no fake stub). */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Receipt</div>
            <div
              style={{ ...styles.linkReceiptBtn, cursor: 'not-allowed', opacity: 0.5 }}
              aria-disabled="true"
              title="Receipt upload is not available yet"
              data-testid="link-receipt-disabled"
            >
              <Link size={14} /> Receipt upload — coming soon
            </div>
          </div>

          {/* Actions */}
          <div style={styles.actions}>
            <button type="button" onClick={onClose} style={styles.cancelBtn}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={!parsedAmount || parsedAmount <= 0 || submitting}
              style={{
                ...styles.submitBtn,
                opacity: (!parsedAmount || submitting) ? 0.6 : 1,
              }}
              data-testid="log-payment-submit"
            >
              <CreditCard size={16} />
              {submitting ? 'Logging...' : `Log ${formatMoney(parsedAmount)} ${isDeposit ? 'Deposit' : 'Payment'}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LogPaymentModal;
